import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import PartnerLayout from '@/components/layouts/PartnerLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Shield, Eye, EyeOff } from 'lucide-react';
import { usePartner } from '@/contexts/PartnerContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PartnerSettings() {
  const { headers } = usePartner();
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNextPw, setShowNextPw] = useState(false);

  const handleChangePassword = async () => {
    if (!pwForm.current || !pwForm.next || !pwForm.confirm) return toast.error('All fields are required.');
    if (pwForm.next.length < 8) return toast.error('New password must be at least 8 characters.');
    if (pwForm.next !== pwForm.confirm) return toast.error('New passwords do not match.');
    setPwLoading(true);
    try {
      await axios.post(`${API}/partner/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.next }, { headers });
      toast.success('Password changed successfully!');
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password.');
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <PartnerLayout>
      <div className="max-w-lg space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="partner-settings-title">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your account security</p>
        </div>

        <Card className="border-border bg-card" data-testid="partner-security-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Change Password
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3" data-testid="partner-change-password-form">
            <div className="relative">
              <Input
                type={showCurrentPw ? 'text' : 'password'}
                placeholder="Current password"
                value={pwForm.current}
                onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
                className="pr-9 text-sm"
                data-testid="partner-current-password-input"
              />
              <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowCurrentPw(v => !v)}>
                {showCurrentPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </div>
            <div className="relative">
              <Input
                type={showNextPw ? 'text' : 'password'}
                placeholder="New password (min 8 chars)"
                value={pwForm.next}
                onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))}
                className="pr-9 text-sm"
                data-testid="partner-new-password-input"
              />
              <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowNextPw(v => !v)}>
                {showNextPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </div>
            <Input
              type="password"
              placeholder="Confirm new password"
              value={pwForm.confirm}
              onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
              className="text-sm"
              data-testid="partner-confirm-password-input"
              onKeyDown={e => e.key === 'Enter' && handleChangePassword()}
            />
            <Button onClick={handleChangePassword} disabled={pwLoading} data-testid="partner-save-password-btn" className="w-full sm:w-auto">
              {pwLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {pwLoading ? 'Saving...' : 'Save Password'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </PartnerLayout>
  );
}
